(() => {
  const ready = (callback) => {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", callback, { once: true });
    } else {
      callback();
    }
  };

  const splitText = () => {
    document.querySelectorAll("[data-split-text]").forEach((element) => {
      if (element.dataset.splitReady) return;
      const text = element.textContent.trim();
      element.setAttribute("aria-label", text);
      element.textContent = "";
      [...text].forEach((character, index) => {
        const span = document.createElement("span");
        span.className = "char";
        span.setAttribute("aria-hidden", "true");
        span.style.transitionDelay = `${Math.min(index * 25, 600)}ms`;
        span.textContent = character === " " ? "\u00A0" : character;
        element.appendChild(span);
      });
      element.dataset.splitReady = "true";
    });
  };

  const revealElements = () => {
    const elements = document.querySelectorAll("[data-reveal], [data-split-text]");
    if (!("IntersectionObserver" in window)) {
      elements.forEach((element) => element.classList.add("is-visible"));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -4% 0px" }
    );
    elements.forEach((element) => observer.observe(element));
  };

  const initHeader = () => {
    const header = document.querySelector("[data-header]");
    if (!header) return;
    const menuToggle = header.querySelector("[data-menu-toggle]");
    const menu = header.querySelector("[data-menu]");
    let previousScroll = window.scrollY;

    const setMenu = (open, restoreFocus = true) => {
      document.body.classList.toggle("menu-open", open);
      menuToggle?.setAttribute("aria-expanded", String(open));
      menu?.setAttribute("aria-hidden", String(!open));
      header.classList.remove("is-hidden");
      if (open) {
        window.setTimeout(() => menu?.querySelector("a")?.focus(), 80);
      } else if (restoreFocus) {
        menuToggle?.focus();
      }
    };

    const updateHeader = () => {
      const currentScroll = window.scrollY;
      header.classList.toggle("is-sticky", currentScroll > 40);
      header.classList.toggle(
        "is-hidden",
        currentScroll > previousScroll && currentScroll > 240 && !document.body.classList.contains("menu-open")
      );
      previousScroll = currentScroll;
    };

    window.addEventListener("scroll", updateHeader, { passive: true });

    if (menuToggle) {
      menuToggle.addEventListener("click", () => {
        setMenu(!document.body.classList.contains("menu-open"));
      });
    }

    header.querySelectorAll("[data-menu] a").forEach((link) => {
      link.addEventListener("click", () => {
        setMenu(false, false);
      });
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && document.body.classList.contains("menu-open")) {
        setMenu(false);
      }
    });

    window.addEventListener("resize", () => {
      if (window.innerWidth > 1100 && document.body.classList.contains("menu-open")) {
        setMenu(false, false);
      }
    }, { passive: true });
  };

  const initHeroTilt = () => {
    const hero = document.querySelector("[data-zero-hero]");
    const stage = hero?.querySelector("[data-tilt-stage]");
    const card = hero?.querySelector("[data-tilt-card]");
    const desktopMotion = window.matchMedia("(hover: hover) and (pointer: fine) and (min-width: 750px)");
    if (!hero || !stage || !card || !desktopMotion.matches || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const move = (event) => {
      const bounds = stage.getBoundingClientRect();
      const x = Math.min(Math.max((event.clientX - bounds.left) / bounds.width, 0), 1);
      const y = Math.min(Math.max((event.clientY - bounds.top) / bounds.height, 0), 1);
      const rotateY = (x - 0.5) * 15;
      const rotateX = (0.5 - y) * 10;
      card.style.transform = `rotateY(${rotateY - 6}deg) rotateX(${rotateX + 2}deg) translate3d(0, 0, 24px)`;
      card.style.setProperty("--mx", `${x * 100}%`);
      card.style.setProperty("--my", `${y * 100}%`);
    };

    const reset = () => {
      card.style.transform = "";
      card.style.setProperty("--mx", "50%");
      card.style.setProperty("--my", "50%");
    };

    stage.addEventListener("pointermove", move);
    stage.addEventListener("pointerleave", reset);

    window.addEventListener(
      "scroll",
      () => {
        const offset = Math.min(window.scrollY * 0.08, 60);
        stage.style.translate = `0 ${offset}px`;
      },
      { passive: true }
    );
  };

  const initLookbook = () => {
    document.querySelectorAll("[data-lookbook]").forEach((lookbook) => {
      const rail = lookbook.querySelector("[data-lookbook-rail]");
      const progress = lookbook.querySelector("[data-lookbook-progress]");
      if (!rail || !progress) return;
      const update = () => {
        const maximum = rail.scrollWidth - rail.clientWidth;
        const value = maximum <= 0 ? 1 : rail.scrollLeft / maximum;
        progress.style.width = `${25 + value * 75}%`;
      };
      rail.addEventListener("scroll", update, { passive: true });
      update();

      let grabbing = false;
      let startX = 0;
      let startScroll = 0;
      rail.addEventListener("pointerdown", (event) => {
        if (event.pointerType === "touch") return;
        grabbing = true;
        startX = event.clientX;
        startScroll = rail.scrollLeft;
        rail.setPointerCapture(event.pointerId);
      });
      rail.addEventListener("pointermove", (event) => {
        if (!grabbing) return;
        rail.scrollLeft = startScroll - (event.clientX - startX);
      });
      rail.addEventListener("pointerup", () => {
        grabbing = false;
      });
      rail.addEventListener("pointercancel", () => {
        grabbing = false;
      });
    });
  };

  const initCursor = () => {
    const cursor = document.querySelector(".zl-cursor");
    if (!cursor || !window.matchMedia("(pointer: fine)").matches) return;
    let targetX = -100;
    let targetY = -100;
    let currentX = -100;
    let currentY = -100;

    document.addEventListener("pointermove", (event) => {
      targetX = event.clientX;
      targetY = event.clientY;
    });

    const animate = () => {
      currentX += (targetX - currentX) * 0.18;
      currentY += (targetY - currentY) * 0.18;
      cursor.style.transform = `translate3d(${currentX - cursor.offsetWidth / 2}px, ${currentY - cursor.offsetHeight / 2}px, 0)`;
      requestAnimationFrame(animate);
    };
    animate();

    document.querySelectorAll("a, button, input, select, [data-tilt-card]").forEach((element) => {
      element.addEventListener("pointerenter", () => cursor.classList.add("is-hover"));
      element.addEventListener("pointerleave", () => cursor.classList.remove("is-hover"));
    });
  };

  const initProduct = () => {
    const section = document.querySelector("[data-product-section]");
    if (!section) return;
    const form = section.querySelector("[data-product-form]");
    const variantSelect = form?.querySelector("[data-variant-select]");
    const json = document.querySelector("[data-product-json]");
    if (!form || !variantSelect || !json) return;

    let variants = [];
    try {
      variants = JSON.parse(json.textContent);
    } catch {
      return;
    }

    const updateVariant = () => {
      const selected = [...form.querySelectorAll(".zl-option")].map((fieldset) => {
        return fieldset.querySelector("input:checked")?.value;
      });
      const variant = variants.find((item) => item.options.every((option, index) => option === selected[index]));
      if (!variant) return;
      variantSelect.value = String(variant.id);
      const isSale = Boolean(variant.compare_at_price && variant.compare_at_price > variant.price);
      const saleCountdown = section.querySelector("[data-zl-sale-countdown]");
      if (saleCountdown) saleCountdown.hidden = !isSale;
      const submit = form.querySelector(".zl-product-form__submit");
      if (submit) {
        submit.disabled = !variant.available;
        const label = submit.querySelector("span");
        if (label) label.textContent = variant.available ? "ADD TO CART" : "SOLD OUT";
      }
      const price = section.querySelector("[data-product-price]");
      if (price && window.Shopify?.formatMoney) {
        const currentPrice = price.querySelector("[data-product-current-price]");
        const comparePrice = price.querySelector("[data-product-compare-price]");
        const saleBadge = price.querySelector("[data-product-sale-badge]");
        if (currentPrice) currentPrice.textContent = window.Shopify.formatMoney(variant.price);
        if (comparePrice) {
          comparePrice.textContent = isSale ? window.Shopify.formatMoney(variant.compare_at_price) : "";
          comparePrice.hidden = !isSale;
        }
        if (saleBadge) saleBadge.hidden = !isSale;
      }
      if (history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set("variant", variant.id);
        history.replaceState({}, "", url);
      }
    };

    form.querySelectorAll(".zl-option input").forEach((input) => {
      input.addEventListener("change", updateVariant);
    });

    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      const submit = form.querySelector(".zl-product-form__submit");
      const label = submit?.querySelector("span");
      const original = label?.textContent;
      if (submit) submit.disabled = true;
      if (label) label.textContent = "ADDING...";
      try {
        const response = await fetch("/cart/add.js", {
          method: "POST",
          headers: { Accept: "application/json" },
          body: new FormData(form)
        });
        if (!response.ok) throw new Error("Cart request failed");
        const cart = await fetch("/cart.js").then((result) => result.json());
        document.querySelectorAll("[data-cart-count]").forEach((count) => {
          count.textContent = cart.item_count;
        });
        if (label) label.textContent = "ADDED TO BAG";
      } catch {
        form.submit();
      } finally {
        window.setTimeout(() => {
          if (submit) submit.disabled = false;
          if (label) label.textContent = original;
        }, 1400);
      }
    });
  };

  const initCollectionSort = () => {
    document.querySelectorAll("[data-sort-by]").forEach((select) => {
      select.addEventListener("change", () => {
        const url = new URL(window.location.href);
        url.searchParams.set("sort_by", select.value);
        window.location.href = url.toString();
      });
    });
  };

  const initViewTransitions = () => {
    if (!document.startViewTransition) return;
    document.addEventListener("click", (event) => {
      const link = event.target.closest("a");
      if (!link || link.target || link.hasAttribute("download")) return;
      const url = new URL(link.href, window.location.href);
      if (url.origin !== window.location.origin || url.hash || event.metaKey || event.ctrlKey) return;
      event.preventDefault();
      document.startViewTransition(() => {
        window.location.href = url.href;
      });
    });
  };

  ready(() => {
    splitText();
    revealElements();
    initHeader();
    initHeroTilt();
    initLookbook();
    initCursor();
    initProduct();
    initCollectionSort();
    initViewTransitions();
  });
})();
